import React, { useEffect, useState } from 'react';
import getConfig from 'next/config';
import { Form, Modal } from 'antd';
import dynamic from 'next/dynamic';
import Field from './Field';
import styled from 'styled-components';
import { toast } from 'react-toastify';

const Feedback = dynamic(() => import('./Feedback'));

const typeTemplate = 'To nie jest poprawny ${type}';

export const validateMessages = {
    default: 'Błąd walidacji',
    required: 'To pole jest wymagane',
    enum: 'To pole musi być mieć wartość jedną z [${enum}]',
    whitespace: 'To pole nie może być puste',
    date: {
        format: 'To jest błędny format daty',
        parse: 'To pole nie jest poprawnym formatem daty',
        invalid: 'To nie jest poprawna data'
    },
    types: {
        string: typeTemplate,
        method: typeTemplate,
        array: typeTemplate,
        object: typeTemplate,
        number: typeTemplate,
        date: typeTemplate,
        boolean: typeTemplate,
        integer: typeTemplate,
        float: typeTemplate,
        regexp: typeTemplate,
        email: typeTemplate,
        url: typeTemplate,
        hex: typeTemplate
    },
    string: {
        len: 'To pole musi mieć dokładnie ${len} znaków',
        min: 'To pole musi mieć co najmniej ${min} znaków',
        max: 'To pole musi mieć nie więcej niż ${max} znaków',
        range: 'To pole musi mieć więcej niż ${min} i mniej niż ${max} znaków'
    },
    number: {
        len: 'Ta wartość musi mieć dokładnie ${len}',
        min: 'Ta wartość nie może być mniejsza niż ${min}',
        max: 'Ta wartość nie może być większa niż ${max}',
        range: 'Ta wartość nie może być mniejsza niż ${min} i większa niż ${max}'
    },
    pattern: {
        mismatch: 'Wartość nie spełnia kryteriów: ${pattern}'
    }
};

interface Props {
    form: DynamicForm;
    onSubmitSuccess?: () => void;
}

interface FormValues {
    [name: string]: string | string[] | number | number[];
}

const FormStyled = styled(Form)`
    div.ant-form-item:last-of-type {
        margin-bottom: 0 !important;
    }
`;

export const PrivacyDisclaimerText = styled.div`
    font-size: 13px;
    color: ${(props) => props.color || props.theme.colors.greyishBrownTwo};
`;

export const ShowPrivacyDisclaimerButton = styled.button`
    background: none;
    border: none;
    font-size: 13px;
    padding: 0;
    cursor: pointer;
    color: ${(props) => props.color || props.theme.colors.primary};
`;

const DynamicForm = ({ form, onSubmitSuccess }: Props): JSX.Element => {
    const [isSent, setSent] = useState(false);
    const [isProcessing, setProcessing] = useState(false);

    useEffect(function () {
        if (!form.allowMultipleSubmits && window.localStorage.getItem(`form_${form.uuid}`)) {
            setSent(true);
        }
    }, []);

    const handleHideOverlay = () => {
        setSent(false);
    };

    const showPrivacyModal = () => {
        Modal.info({
            okText: 'Zamknij',
            width: '80%',
            title: 'Informacja o przetwarzaniu danych osobowych',
            content: <div dangerouslySetInnerHTML={{ __html: form.privacyDisclaimer }} />
        });
    };

    const onFinish = (values: FormValues) => {
        setProcessing(true);
        const getLocationOrigin = () => {
            const { href } = window.location;
            return href;
        };
        const { apiUrl } = getConfig().publicRuntimeConfig;
        fetch(`${apiUrl}/public/api/dynamic-form/${form.uuid}/submit`, {
            method: 'post',
            headers: {
                Accept: 'application/json, text/plain, */*',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ data: values, url: getLocationOrigin() })
        })
            .then(() => {
                toast.success(
                    <>
                        <h3>Dziękujemy za kontakt.</h3>
                        <p>Wiadomość wysłana</p>
                    </>
                );
                if (!form.allowMultipleSubmits) {
                    setSent(true);
                }

                if (typeof window !== 'undefined') {
                    window.localStorage.setItem(`form_${form.uuid}`, 'true');
                }
                if (typeof onSubmitSuccess !== 'undefined') {
                    onSubmitSuccess();
                }
            })
            .finally(() => {
                setProcessing(false);
            });
    };

    const onFinishFailed = () => {
        toast.success(
            <>
                <h3>Błąd.</h3>
                <p>W formularzu wystąpiły błędy</p>
            </>
        );
    };

    return (
        <div style={{ background: '#f4f4f4' }}>
            {isSent && <Feedback type={form.type} hideOverlay={handleHideOverlay} />}
            <FormStyled
                name={form.name}
                layout="vertical"
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                autoComplete="off"
                style={{ padding: '15px' }}
                validateMessages={validateMessages}
                scrollToFirstError={true}
            >
                {form.config.map((field) => (
                    <Field
                        key={`form-${form.name}-field-${field.label}`}
                        config={field}
                        formState={{ isSent, isProcessing }}
                    />
                ))}
                {form.showPrivacyDisclaimer && (
                    <PrivacyDisclaimerText>
                        Wypełniając formularz, akceptujesz{' '}
                        <ShowPrivacyDisclaimerButton type="button" onClick={showPrivacyModal}>
                            regulamin.
                        </ShowPrivacyDisclaimerButton>
                    </PrivacyDisclaimerText>
                )}
            </FormStyled>
        </div>
    );
};

export default DynamicForm;
